import React, { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring, animate } from "framer-motion";
import {
  Terminal,
  Rocket,
  Briefcase,
  Bot,
  Code,
  ChevronDown,
  CheckCircle2,
} from "lucide-react";
import { Link } from "react-router-dom";

const AnimatedCounter = ({ value, label, suffix = "" }) => {
  const nodeRef = useRef();

  useEffect(() => {
    const node = nodeRef.current;
    if (!node) return;

    // Framer motion's animate function for smooth counting
    const controls = animate(0, value, {
      duration: 2.5,
      ease: "easeOut",
      onUpdate(v) {
        node.textContent = Math.round(v) + suffix;
      },
    });

    return () => controls.stop();
  }, [value, suffix]);

  return (
    <div className="flex flex-col items-center justify-center p-4">
      <span
        ref={nodeRef}
        className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-br from-white to-slate-400"
      >
        0{suffix}
      </span>
      <span className="text-xs md:text-sm text-slate-400 font-medium tracking-wide mt-1 uppercase">
        {label}
      </span>
    </div>
  );
};

const Hero = () => {
  // Smooth Mouse Tracking for the Background Glow
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  useEffect(() => {
    const handleMouseMove = (e) => {
      // Calculate offset from center of screen
      mouseX.set(e.clientX - window.innerWidth / 2);
      mouseY.set(e.clientY - window.innerHeight / 2);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [mouseX, mouseY]);

  // Animation variants for staggered entrance
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.3 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const floatingAnimation = {
    y: ["-10px", "10px", "-10px"],
    transition: { duration: 4, repeat: Infinity, ease: "easeInOut" },
  };

  return (
    <section className="relative min-h-screen text-slate-200 overflow-hidden">
      {/* BACKGROUND: Grid & Glow */}
      {/* <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div> */}

      {/* Dynamic Mouse-following Glow */}
      {/* <motion.div
        style={{ x: smoothX, y: smoothY }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vw] h-[60vw] bg-purple-600/20 rounded-full blur-[120px] opacity-50 pointer-events-none"
      ></motion.div>
      <div className="absolute top-1/4 right-1/4 w-[40vw] h-[40vw] bg-blue-600/20 rounded-full blur-[100px] opacity-40 pointer-events-none"></div> */}

      {/* MAIN CONTENT WRAPPER */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 lg:pt-40 lg:pb-28 flex flex-col min-h-screen justify-between">
        {/* HERO SPLIT (Left / Right) */}
        <div className="flex flex-col lg:flex-row items-center justify-between gap-16 lg:gap-8">
          {/* LEFT: Intro & CTA */}
          <motion.div
            className="w-full lg:w-1/2 flex flex-col items-start"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div
              variants={itemVariants}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-6"
            >
              <span className="animate-wave inline-block origin-bottom-right">
                👋
              </span>
              <span className="text-sm text-purple-300 font-medium tracking-wide">
                Hey there, I'm
              </span>
            </motion.div>

            <motion.h1
              variants={itemVariants}
              className="text-5xl md:text-7xl font-extrabold tracking-tight mb-4"
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-100 via-purple-100 to-slate-100">
                JATIN{" "}
              </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-500">
                PORIYA
              </span>
            </motion.h1>

            <motion.h2
              variants={itemVariants}
              className="text-2xl md:text-3xl font-bold text-slate-300 mb-6 leading-tight"
            >
              Full Stack Developer, <br />
              <span className="text-slate-400">
                Product Builder & <br className="hidden md:block" />
                Creative Problem Solver.
              </span>
            </motion.h2>

            <motion.p
              variants={itemVariants}
              className="text-lg text-slate-400 max-w-xl mb-10 leading-relaxed"
            >
              I build scalable web applications, modern user experiences, and
              AI-powered products that solve real-world problems.
            </motion.p>

            <motion.div
              variants={itemVariants}
              className="flex flex-wrap gap-4"
            >
              <Link to="/projects">
                <button className="group relative px-6 py-3 font-semibold text-white rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 hover:opacity-90 transition-all hover:shadow-[0_0_20px_rgba(147,51,234,0.4)] flex items-center gap-2">
                  <Rocket className="w-5 h-5 group-hover:-translate-y-1 transition-transform" />
                  Explore My Work
                </button>
              </Link>

              <Link
                to="https://wa.me/917201840095?text=Hi%20Jatin!%20I%20visited%20your%20portfolio%20and%20I%27m%20interested%20in%20working%20with%20you.%20I%20have%20a%20project%20idea%20I%27d%20love%20to%20discuss.%20Let%27s%20connect!"
                target="_blank"
                rel="noopener noreferrer"
              >
                <button className="px-6 py-3 font-semibold text-slate-200 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-all flex items-center gap-2">
                  <Code className="w-5 h-5" />
                  🤝 Let's Work Together
                </button>
              </Link>
            </motion.div>
          </motion.div>

          {}
          {/* RIGHT: Visuals & Floating Cards */}
          <motion.div
            className="w-full lg:w-1/2 relative h-[500px] flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Center Avatar / Graphic */}
            <div className="relative flex flex-col items-center">
              <div className="relative w-64 h-64 md:w-80 md:h-80 rounded-full bg-gradient-to-tr from-purple-600/30 to-blue-600/30 border border-white/10 backdrop-blur-md flex items-center justify-center overflow-hidden shadow-[0_0_40px_rgba(147,51,234,0.3)] z-10 hover:shadow-[0_0_60px_rgba(147,51,234,0.5)] transition-shadow duration-500">
                {/* Subtle glowing pulse behind the image */}
                <div className="absolute inset-0 bg-gradient-to-tr from-purple-500/20 to-blue-500/20 rounded-full animate-pulse z-0"></div>

                {/* YOUR PROFILE IMAGE */}
                <img
                  src="/jatinporiya.webp"
                  alt="Jatin Poriya"
                  className="relative z-10 w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500 ease-in-out"
                />

                {/* Inner shadow image ko background se blend karne ke liye */}
                <div className="absolute inset-0 rounded-full shadow-[inset_0_0_20px_rgba(0,0,0,0.5)] z-20 pointer-events-none"></div>
              </div>

              {/* Status Chip */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="absolute -bottom-5 bg-[#0f172a]/90 backdrop-blur-xl border border-white/10 px-5 py-2 rounded-full flex items-center gap-2 z-30 shadow-xl"
              >
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)] animate-pulse"></div>
                <span className="text-xs md:text-sm font-semibold text-slate-200 tracking-wide">
                  Open to Contribute
                </span>
              </motion.div>
            </div>

            {/* Floating Terminal Widget */}
            <motion.div
              animate={floatingAnimation}
              whileHover={{ scale: 1.05, rotate: -2, zIndex: 40 }}
              className="absolute -left-4 md:left-4 top-10 p-4 rounded-xl bg-[#0f172a]/80 backdrop-blur-md border border-white/10 shadow-xl w-52 z-20 transition-all cursor-default"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
              </div>
              <p className="text-xs font-mono text-green-400">{">"} whoami</p>
              <p className="text-xs font-mono text-slate-300 mt-1">
                Jatin Poriya
              </p>
              <p className="text-xs font-mono text-slate-400 mt-1 leading-snug">
                Building Products &<br />
                Creative Experiences
              </p>
            </motion.div>

            {/* Floating Internship/Work Card */}
            <motion.div
              animate={{
                y: ["10px", "-10px", "10px"],
                transition: {
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
              }}
              whileHover={{ scale: 1.05, rotate: 2, zIndex: 40 }}
              className="absolute -right-4 md:-right-8 top-1/4 p-3 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 shadow-xl flex items-center gap-3 z-20 transition-all cursor-default"
            >
              <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400">
                <Briefcase className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-200">
                  Remote Intern
                </p>
                <p className="text-xs text-slate-400">Full Stack Dev</p>
              </div>
            </motion.div>

            {/* Currently Building Widget */}
            <motion.div
              animate={floatingAnimation}
              whileHover={{ scale: 1.05, rotate: -1, zIndex: 40 }}
              className="absolute left-8 -bottom-8 md:bottom-2 p-4 rounded-xl bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-md border border-white/10 shadow-xl z-20 transition-all cursor-default"
            >
              <p className="text-xs font-bold text-purple-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                <Bot className="w-4 h-4" /> Current Focus
              </p>
              <ul className="text-sm text-slate-300 space-y-1">
                <li className="flex items-center gap-2">
                  <span className="text-purple-500">•</span> AI Products
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-blue-500">•</span> Modern UI
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-teal-500">•</span> Scalable SaaS
                </li>
              </ul>
            </motion.div>
          </motion.div>
        </div>

        {}
        {/* BOTTOM: Trust Bar, Counters & Scroll Indicator */}
        <motion.div
          className="mt-28 md:mt-24 flex flex-col items-center w-full"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
        >
          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-3 md:gap-6 mb-8 text-sm md:text-base text-slate-400">
            {[
              "Full Stack Developer",
              "Internship Experience",
              "Product Builder",
              "AI Enthusiast",
            ].map((text, i) => (
              <div
                key={i}
                className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full border border-white/5 hover:bg-white/10 transition-colors cursor-default"
              >
                <CheckCircle2 className="w-4 h-4 text-purple-400" />
                <span>{text}</span>
              </div>
            ))}
          </div>

          {/* Animated Trust Counters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-16 mb-12 w-full max-w-4xl border-y border-white/5 py-6">
            <AnimatedCounter value={10} label="Projects Built" suffix="+" />
            <AnimatedCounter value={4} label="Internship" suffix="" />
            <AnimatedCounter value={3} label="Clients" suffix="+" />
            <AnimatedCounter value={500} label="Github Commits" suffix="+" />
          </div>

          {/* Core Philosophy Quote */}
          <div className="text-center max-w-2xl px-4 relative mt-4">
            <span className="absolute -top-6 -left-2 text-5xl text-purple-500/20 font-serif">
              "
            </span>
            <p className="text-xl md:text-2xl text-slate-200 font-medium italic tracking-wide leading-relaxed">
              Turning ideas into products, <br className="hidden md:block" />
              <span className="text-slate-400">
                and products into experiences.
              </span>
            </p>
            <span className="absolute -bottom-8 -right-2 text-5xl text-blue-500/20 font-serif">
              "
            </span>
          </div>

          {/* Scroll Down */}
          <motion.div
            className="mt-16 flex flex-col items-center gap-2 cursor-pointer text-slate-500 hover:text-purple-400 transition-colors"
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <span className="text-sm font-medium tracking-widest uppercase">
              Scroll to explore my journey
            </span>
            <ChevronDown className="w-5 h-5" />
          </motion.div>
        </motion.div>
      </div>

      {/* Custom CSS for the waving hand */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes wave {
          0% { transform: rotate(0.0deg) }
          10% { transform: rotate(14.0deg) }
          20% { transform: rotate(-8.0deg) }
          30% { transform: rotate(14.0deg) }
          40% { transform: rotate(-4.0deg) }
          50% { transform: rotate(10.0deg) }
          60% { transform: rotate(0.0deg) }
          100% { transform: rotate(0.0deg) }
        }
        .animate-wave {
          animation: wave 2.5s infinite;
        }
      `,
        }}
      />
    </section>
  );
};

export default Hero;
